import ApolloClient from 'apollo-client'
import * as Sentry from '@sentry/browser'

import {
  getUserAuthorization,
  replaceAuthorization,
} from 'graphql/multisession'
import {
  RefreshCredentialsDocument,
  RefreshCredentialsMutation,
  RefreshCredentialsMutationVariables,
} from 'gqlTypes'

export const checkIfTokenIsExpired = (expirationDateTime: string) =>
  new Date(expirationDateTime) < new Date()

const fireRefreshAccessTokenMutation = async (
  client: ApolloClient<object>,
  authToken: string,
  refreshToken: string
) => {
  const { data, errors } = await client.mutate<
    RefreshCredentialsMutation,
    RefreshCredentialsMutationVariables
  >({
    mutation: RefreshCredentialsDocument,
    variables: { authToken, refreshToken },
    update: (cache, { data }) => {
      const { credentials } = data?.refreshCredentials || {}

      if (credentials) {
        replaceAuthorization(cache, credentials, authToken)
      }
    },
  })

  const { credentials } = data?.refreshCredentials || {}

  if (errors || !credentials) {
    throw new Error('It was not possible to refresh the user credentials')
  }
}

let refreshPromise: Promise<void> | null

export const refreshToken = async function startRefreshToken(
  client: ApolloClient<object>
) {
  const credentials = getUserAuthorization(client.cache)

  try {
    // Check if there isn't already a token refreshing, if there isn't create a request dependency
    // so the next requests can relay on
    if (!refreshPromise) {
      refreshPromise = fireRefreshAccessTokenMutation(
        client,
        credentials!.authToken,
        credentials!.refreshToken
      )
    }

    await refreshPromise
    Sentry.addBreadcrumb({
      category: 'Authentication',
      message: `Token refreshed`,
      level: Sentry.Severity.Log,
    })
    return true
  } catch (error) {
    Sentry.addBreadcrumb({
      category: 'Authentication',
      message: `Refresh token request failed with error: ${error.message}`,
      level: Sentry.Severity.Error,
    })
    return false
  } finally {
    // eslint-disable-next-line require-atomic-updates
    refreshPromise = null
  }
}
